import { Stack, Typography, useTheme } from "@mui/material";
import {
  LeafIcon,
  BigChoiceModal,
  ConfirmationModal,
  ScanningIndicator,
  useScanIndicator,
  useToast,
  AlertBanner
} from "@qubit/autoparts";
import * as Sentry from "@sentry/react";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import FullPageLayout from "~/components/layout/FullPageLayout";
import useFlag from "~/config/flags";
import { createProductSearchOptions } from "~/config/searchProductConfig";
import { InventoryProductCard } from "~/features/inventoryProductCard/InventoryProductCard";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useInactivityResetTimer } from "~/hooks/useInactivityResetTimer";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import usePortStatus from "~/hooks/usePortStatus";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import { getBinCompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import { fetchWarehouseBinIds } from "~/lib/fetchWarehouseBinIds";
import {
  generateInventoryPath,
  isAutostoreView,
  runAsyncSafely,
  searchProduct
} from "~/lib/helpers";
import { useGridV2Subscription } from "~/lib/signalr";
import { TempZone } from "~/lib/tempZone";
import { nonNullable } from "~/lib/typeHelpers";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";

import {
  fetchPortStatus,
  closeBin,
  closePort,
  GetPortResponse,
  binAtPortEvent,
  binNotAtPort,
  fetchBinLogPublisherState,
  setPortStatus,
  setSelectedAutostoreBinId,
  getNextBinWithConfig,
  closeWorkstation,
  setCurrentEmptyBin
} from "~/redux/actions/autostore";
import { getCycleCountFrequencyByVid } from "~/redux/actions/cycleCounts";
import {
  getInventorySummaries,
  getVariantByVariantId,
  clearSelectedVariant,
  clearSelectedInventoryId,
  clearInventory,
  getInventoryReport,
  clearInventoryToModify,
  requestBinsForMultiport
} from "~/redux/actions/inventory";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import {
  selectPortStateByPort,
  selectSelectedInventoryBin
} from "~/redux/selectors/autostoreSelectors";
import {
  selectIsAdjustingBins,
  selectHoldTypeFilters,
  selectTempZoneFilters,
  selectSearchedBinIds,
  selectSelectedInventoryRows,
  selectSelectedSummaries,
  selectInventorySummariesToDisplay,
  selectSelectedInventoryAtPort,
  selectInventorySummaryToDisplay,
  selectAdjustingInventory
} from "~/redux/selectors/inventorySelectors";
import { selectUserMessages } from "~/redux/selectors/siteSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectNextEmptyBinByPort,
  selectSelectedPortId,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { warehouseApi } from "~/redux/warehouse/warehouseApi";
import { AutostoreEvent } from "~/types/api";

import { AutostorePutawayModal } from "./AutostorePutawayModal";
import { BottomActionButtons } from "./BottomActionButtons";
import CycleCountFrequencyButton from "./CycleCountFrequencyButton";
import { InventoryAdjustDialog } from "./InventoryAdjustDialog";
import { InventoryMultiPort } from "./InventoryMultiPort";
import { InventoryNavbarSearch } from "./InventoryNavbarSearch";
import { InventorySummaryHeader } from "./InventorySummaryHeader";
import { InventoryTables, limit } from "./InventoryTables";
import { TopActionButtons } from "./TopActionButtons";
import { getBinIds } from "./getBinIds";
import {
  incrementBinAtPortSeconds,
  resetBinAtPortSeconds,
  setAllInventoryPage,
  setIsAutostorePutawayDialogOpen,
  setIsAdjustingBins,
  setIsInventoryAdjustDialogOpen,
  setIsCreateInventoryButtonEnabled,
  setIsFetchingBin,
  setIsGetBinsLoading,
  setIsMovePanelOpen,
  setPortPollingActive,
  setSelectedAdjustingSummary,
  setSelectedCompartment,
  setSelectedSummaries,
  setHoldTypeFilters,
  setTempZoneFilters,
  setSearchedBinIds
} from "./inventory.slice";
import { useAutocompleteSelect } from "./useAutocompleteSelect";
import { useGetInventoryParams } from "./useGetInventoryParams";
import { useGridSelectorWrapper } from "./useGridSelectorWrapper";
import { useNextEmptyBin } from "./useNextEmptyBin";
import { useViewType } from "./useViewType";

type BinSearchData = {
  autostore_bin_number: number;
  warehouse_bin_ids: Guid[];
  type?: string;
};

type Props = { viewTitle?: ViewNameTranslation };

export type BinOrProductResult = {
  type: "bin" | "product";
  variantId?: Guid;
  binId?: Guid;
  displayText: string;
};

export function AutostoreInventory(props: Props) {
  const { viewTitle } = props;

  // hooks
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const { palette } = useTheme();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  const dispatch = useAppDispatch();
  const navigate = useFcNavigate();
  const locationInfo = useLocation();
  const { pathname, search } = locationInfo;
  const isAutostoreInventoryView = isAutostoreView(search);
  const { isHoldsPage, isProductView, viewType } = useViewType();
  const { variantId: variantIdParam, binNumber: binNumberParam } = useParams<{
    variantId?: string;
    binNumber?: string;
  }>();
  const handleAutocompleteSelect = useAutocompleteSelect();
  const { gridSelectorButton, autostoreGridOverride } =
    useGridSelectorWrapper();
  const { getInventoryParams } = useGetInventoryParams();

  const hasInventoryTableV2EnhancedFlag =
    useFlag().tablesV2Enhanced.includes("inventoryTablesV2");

  const searchAndFilterInventory = useMemo(() => <InventoryNavbarSearch />, []);
  const { setMenuItems } = useNavbar({
    centerComponent: !hasInventoryTableV2EnhancedFlag
      ? searchAndFilterInventory
      : undefined,
    viewTitle,
    rightComponent: gridSelectorButton
  });

  const nextEmptyBin = useNextEmptyBin();

  const clientId = useAppSelector(selectUsersClientId);
  const isAdjustingBins = useAppSelector(selectIsAdjustingBins);
  const holdTypeFilters = useAppSelector(selectHoldTypeFilters);
  const tempZoneFilters = useAppSelector(selectTempZoneFilters);
  const inventoryReports = useAppSelector(
    (state) => state.inventory.inventoryReport
  );
  const inventorySummaries = useAppSelector(
    (state) => state.inventory.inventorySummaries
  );
  const selectedSummaries = useAppSelector(selectSelectedSummaries);
  const allInventoryPage = useAppSelector(
    (state) => state.inventoryNew.allInventoryPage
  );
  const binAtPortSeconds = useAppSelector(
    (state) => state.inventoryNew.binAtPortSeconds
  );
  const isAutostorePutawayDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isAutostorePutawayDialogOpen
  );
  const isGetBinsLoading = useAppSelector(
    (state) => state.inventoryNew.isGetBinsLoading
  );
  const selectedAdjustingInventory = useAppSelector(selectAdjustingInventory);
  const isChangeBinConfigModalOpen = useAppSelector(
    (state) => state.inventoryNew.isChangeBinConfigModalOpen
  );
  const isFetchingBin = useAppSelector(
    (state) => state.inventoryNew.isFetchingBin
  );
  const isCreateInventoryButtonEnabled = useAppSelector(
    (state) => state.inventoryNew.isCreateInventoryButtonEnabled
  );
  const portPollingActive = useAppSelector(
    (state) => state.inventoryNew.portPollingActive
  );
  const portState = useAppSelector((state) => state.autostore.portState);
  const portStateByPort = useAppSelector(selectPortStateByPort);
  const isInventoryAdjustDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isInventoryAdjustDialogOpen
  );
  const initialFrequency = useAppSelector(
    (state) => state.cycleCounts.cycleCountFrequency?.frequency
  );
  const inventorySummaryToDisplay = useAppSelector(
    selectInventorySummaryToDisplay
  );
  const nextBinInventoryByPort = useAppSelector(
    (state) => state.autostore.nextBinInventoryByPort
  );
  const nextEmptyBinByPort = useAppSelector(selectNextEmptyBinByPort);
  const selectedAutostoreBinId = useAppSelector(
    (state) => state.autostore.selectedAutostoreBinId
  );
  const selectedRows = useAppSelector(selectSelectedInventoryRows);
  const selectedInventoryAtPort = useAppSelector(selectSelectedInventoryAtPort);
  const summariesToDisplay = useAppSelector(selectInventorySummariesToDisplay);
  const selectedGridIdFromRedux = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const selectedPortId = useAppSelector(selectSelectedPortId);
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const siteAllPortIds = useAppSelector(
    (state) => state.workstations.siteAllPortIds
  );
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const thisWorkstationId = useAppSelector(selectWorkstationId);
  const userMessages = useAppSelector(selectUserMessages);
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);

  const selectedAutostoreGridId = siteWorkstation
    ? selectedGridIdFromRedux
    : autostoreGridOverride;
  const searchedBinIds = useAppSelector(selectSearchedBinIds);

  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);

  // adjust specific bin
  const [specificBinAtPort, setSpecificBinAtPort] = useState<number | null>(
    null
  );

  const [scanChoices, setScanChoices] = useState<BinOrProductResult[]>(
    [] as BinOrProductResult[]
  );
  const [showScanChoiceModal, setShowScanChoiceModal] =
    useState<boolean>(false);

  const [isNavigateAwayModalOpen, setIsNavigateAwayModalOpen] = useState(false);
  const [isNoInventoryBin, setIsNoInventoryBin] = useState(false);
  const [scanState, setScanState] = useScanIndicator();

  const currentSelectedBin = useAppSelector(selectSelectedInventoryBin);
  const currentSelectedPortId = currentSelectedBin?.portId;

  const { areAllPortsReady, firstPort, portStateByPortArray } = usePortStatus(
    portStateByPort,
    siteAllPortIds,
    currentSelectedPortId
  );

  const offset = (allInventoryPage - 1) * limit;

  const isAnySelect: boolean = selectedRows.length >= 1;
  const allSelectedBinTypeAreAutostore = selectedSummaries.every(
    (summary) => summary?.binType === "autostore"
  );
  const selectedBinNumbers = selectedSummaries
    ?.map((summary) => summary?.autostoreBinNumber)
    .filter(nonNullable);

  const onInactivityModalClose = useCallback(() => {
    dispatch(setIsAutostorePutawayDialogOpen(false));
    dispatch(clearInventoryToModify());
    dispatch(setIsInventoryAdjustDialogOpen(false));
    dispatch(setIsMovePanelOpen(false));
  }, [dispatch]);

  const { restartInactivityTimer } = useInactivityResetTimer({
    onInactivityModalClose
  });

  useDevCheats({
    isPortPolling: portPollingActive,
    showAutostoreStatus: isAutostoreInventoryView
  });

  const clearSelectedData = () => {
    dispatch(clearSelectedVariant());
    dispatch(clearSelectedInventoryId());
    dispatch(setCurrentEmptyBin(null));
  };

  const resetView = (resetInv = true) => {
    if (resetInv) {
      clearSelectedData();
      dispatch(clearInventory());
      dispatch(setSelectedSummaries([]));
    }
    if (!shouldListenToGridEvents) {
      dispatch(setPortPollingActive(false));
    } else {
      dispatch(binAtPortEvent());
    }
    dispatch(setIsFetchingBin(false));
    dispatch(setIsCreateInventoryButtonEnabled(true));
    dispatch(setSelectedAdjustingSummary(null));
    dispatch(setAllInventoryPage(1));
  };

  // adjusting specific bin
  useEffect(() => {
    const specificBinResponse = Object.values(nextBinInventoryByPort).find(
      (bin) => bin.nextBinResponse?.openBinResponse.binId === specificBinAtPort
    );

    const compartmentOccupancy =
      getBinCompartmentOccupancy(specificBinResponse);

    const emptyCompartments =
      compartmentOccupancy
        .filter((b) => b.isCompartmentEmpty)
        .map((b) => b.autostoreCompartmentNumber)
        .filter((cn): cn is number => cn !== undefined) ?? [];

    if (
      isAutostorePutawayDialogOpen &&
      specificBinResponse &&
      !emptyCompartments?.length
    ) {
      errorToast(t("bin is full"));
      dispatch(setIsGetBinsLoading(false));
      dispatch(setIsAutostorePutawayDialogOpen(false));
      dispatch(setIsAdjustingBins(false));

      void dispatch(closeWorkstation());
    }
    if (
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      specificBinAtPort &&
      nextBinInventoryByPort &&
      specificBinResponse &&
      emptyCompartments?.length &&
      emptyCompartments.length > 0
    )
      dispatch(
        setSelectedCompartment({
          bin: specificBinResponse.nextBinResponse,
          compartmentNumber: emptyCompartments[0] - 1 // select first empty compartment
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specificBinAtPort, nextBinInventoryByPort]);

  // component did unmount
  useEffect(
    () => () => {
      resetView(false);
      if (areAllPortsReady && shouldListenToGridEvents) {
        dispatch(binNotAtPort());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    dispatch(
      setSelectedAutostoreBinId(
        inventorySummaryToDisplay?.autostoreBinNumber || null
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySummaryToDisplay]);

  // EFFECT 1: stop polling when bin arrives //
  useEffect(() => {
    if (!shouldListenToGridEvents && areAllPortsReady) {
      dispatch(setPortPollingActive(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllPortsReady]);

  useEffect(() => {
    if (
      pathname === "/autostore-inventory/bin" ||
      pathname === "/autostore-inventory/product"
    ) {
      dispatch(clearSelectedVariant());
      dispatch(clearSelectedInventoryId());
      dispatch(clearInventory());
    }
    if (!shouldListenToGridEvents) {
      dispatch(setPortPollingActive(false));
    } else if (areAllPortsReady) {
      dispatch(binNotAtPort());
    }

    dispatch(setIsFetchingBin(false));
    dispatch(setAllInventoryPage(1));
  }, [pathname, shouldListenToGridEvents, areAllPortsReady, dispatch]);

  // clear filters on main inventory page mount (not on product or bin pages)
  useEffect(() => {
    if (
      pathname === "/autostore-inventory" ||
      pathname === "/autostore-inventory/holds"
    ) {
      dispatch(setHoldTypeFilters([]));
      dispatch(setTempZoneFilters([]));
      dispatch(setSearchedBinIds(null));
    }
    // empty dependency array to only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If navigating to a bin page, clear the searched bin IDs
  useEffect(() => {
    if (binNumberParam) {
      dispatch(setSearchedBinIds(null));
    }
  }, [binNumberParam, dispatch]);

  const handleGetInventorySummaries = useCallback(
    async (args: {
      variantId?: Guid;
      searchedBinIds?: Guid | Guid[];
      offsetZero?: boolean;
      holds?: string[];
      tempZones?: TempZone[];
    }) => {
      return runAsyncSafely(async () => {
        const { variantId, searchedBinIds, offsetZero, holds, tempZones } =
          args;
        await dispatch(
          getInventorySummaries({
            variantId,
            binId: searchedBinIds,
            limit,
            offset: offsetZero ? 0 : offset,
            autostoreGridId: getInventoryParams.autostoreGridId,
            hasHold: getInventoryParams.hasHold,
            hold: holds,
            temperatureZones: tempZones
          })
        );
      });
    },
    [dispatch, offset, getInventoryParams]
  );

  const setBinInvSummaries = async (
    binNumberParam: string,
    holds?: string[],
    tempZones?: TempZone[]
  ) => {
    return runAsyncSafely(async () => {
      if (selectedAutostoreGridId && clientId && usersFulfillmentCenter) {
        const warehouseBinIds = await fetchWarehouseBinIds({
          selectedAutostoreGridId,
          clientId,
          usersFulfillmentCenter,
          binNumberParam
        });
        if (warehouseBinIds) {
          dispatch(setSearchedBinIds(warehouseBinIds));

          await handleGetInventorySummaries({
            searchedBinIds: warehouseBinIds,
            offsetZero: true,
            holds,
            tempZones
          });
        }
      }
    });
  };

  const handleRefetchInventory = async (
    holds?: string[],
    offsetZero?: boolean,
    tempZones?: TempZone[]
  ) => {
    if (variantIdParam) {
      await handleGetInventorySummaries({
        variantId: variantIdParam,
        holds,
        offsetZero,
        tempZones
      });
    } else if (binNumberParam) {
      await setBinInvSummaries(binNumberParam, holds, tempZones);
    } else {
      await handleGetInventorySummaries({
        holds,
        offsetZero,
        tempZones
      });
    }
  };

  // * fetch inventory, etc when url changes
  useEffect(() => {
    if (variantIdParam) {
      void handleGetInventorySummaries({
        variantId: variantIdParam,
        holds: holdTypeFilters,
        tempZones: tempZoneFilters
      });
    } else if (binNumberParam) {
      void setBinInvSummaries(binNumberParam);
    } else if (limit || allInventoryPage)
      void handleRefetchInventory(holdTypeFilters, undefined, tempZoneFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantIdParam, binNumberParam, limit, allInventoryPage]);

  const previousHoldTypeFilters = useRef<string[] | undefined>(holdTypeFilters);
  const previousTempZoneFilters = useRef<TempZone[] | undefined>(
    tempZoneFilters
  );

  // if we're adjusting bins, don't reset the page
  useEffect(() => {
    if (isAdjustingBins) return;

    // Only refetch if filters changed
    void (async () => {
      const holdTypeFiltersChanged =
        JSON.stringify(previousHoldTypeFilters.current) !==
        JSON.stringify(holdTypeFilters);
      const tempZoneFiltersChanged =
        JSON.stringify(previousTempZoneFilters.current) !==
        JSON.stringify(tempZoneFilters);

      if (holdTypeFiltersChanged || tempZoneFiltersChanged) {
        await handleRefetchInventory(holdTypeFilters, true, tempZoneFilters);
        dispatch(setAllInventoryPage(1));
        previousHoldTypeFilters.current = holdTypeFilters;
        previousTempZoneFilters.current = tempZoneFilters;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdTypeFilters, tempZoneFilters, isAdjustingBins]);

  // get variant info for selected inventory, or variant from params if no inventory records
  const searchVariantId: Guid | null =
    (isAdjustingBins && selectedInventoryAtPort?.variantId) ||
    variantIdParam ||
    (!isAdjustingBins && inventorySummaryToDisplay?.variantId) ||
    null;

  useEffect(() => {
    if (searchVariantId) {
      void dispatch(getVariantByVariantId(searchVariantId));
    } else if (selectedAdjustingInventory) {
      void dispatch(
        getVariantByVariantId(selectedAdjustingInventory.variantId)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVariantId, selectedAdjustingInventory, variantIdParam]);

  // get inventory report for selected variant. Used for total qty/total committed
  useEffect(() => {
    if (selectedVariant) {
      void dispatch(getInventoryReport(selectedVariant.variantId));
      void dispatch(getCycleCountFrequencyByVid(selectedVariant.variantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  const createBinSearchOptions = (binRecord: BinSearchData[]) =>
    binRecord.map((b: { autostore_bin_number: number }) => ({
      displayText: String(b.autostore_bin_number),
      type: "bin" as "bin" | "product"
    }));

  interface ScanObj {
    scanType: "bin result" | "product result" | "products result";
    binOrProductResult?: BinOrProductResult;
    productsResult?: BinOrProductResult[];
  }

  useKeyDownHandler();
  useBarcodeScanner<ScanObj | null>({
    disabled: isAutostorePutawayDialogOpen || isInventoryAdjustDialogOpen,
    findScanMatch: async (buffer: string) => {
      // prevent scan from firing when adjusting bins
      if (isAdjustingBins && selectedRows.length) return null;
      restartInactivityTimer();
      setScannedBarcode(buffer);
      setScanState("success");
      const hits = clientId ? await searchProduct(buffer) : [];
      // alert user if no results are returned from Product Search
      if (!hits.length)
        errorToast(
          t("no product exists for") +
            buffer +
            ". " +
            t("please set product aside")
        );
      const hitsAsAutocompleteRecords = createProductSearchOptions({
        hits,
        exactUpcMatchFilter: buffer
      });
      if (hitsAsAutocompleteRecords.length === 1) {
        return {
          scanType: "product result",
          binOrProductResult: hitsAsAutocompleteRecords[0]
        };
      }
      if (hitsAsAutocompleteRecords.length > 1) {
        return {
          scanType: "products result",
          productsResult: hitsAsAutocompleteRecords
        };
      }

      const binData = await getBinIds({
        searchText: buffer,
        usersClientId: clientId,
        usersFulfillmentCenter,
        usersAutostoreGridId: selectedAutostoreGridId
      });

      dispatch(setSearchedBinIds(binData[0]?.warehouse_bin_ids));
      const binsAsAutocompleteRecords = createBinSearchOptions(binData);
      const matchedBinRecord = binsAsAutocompleteRecords.find(
        (obj) => obj.displayText === buffer
      );
      if (matchedBinRecord) {
        return {
          scanType: "bin result",
          binOrProductResult: matchedBinRecord
        };
      }
      return null;
    },
    processScanMatch: (scanObj) => {
      if (!scanObj) return null;
      if (isAutostorePutawayDialogOpen) return null;
      if (scanObj.scanType === "products result" && scanObj.productsResult) {
        setScanChoices(scanObj.productsResult);
        setShowScanChoiceModal(true);
        return null;
      }

      const matchedItem = scanObj.binOrProductResult;
      if (!matchedItem) {
        return null;
      }
      setScanState("success");
      if (scanObj.scanType === "product result") {
        navigate({
          pathname: generateInventoryPath(
            "product",
            isHoldsPage,
            matchedItem.variantId as string
          ),
          search
        });
      } else if (scanObj.scanType === "bin result") {
        dispatch(clearSelectedVariant());
        navigate({
          pathname: generateInventoryPath(
            "bin",
            isHoldsPage,
            matchedItem.displayText
          ),
          search
        });
      }
      return null;
    },
    deps: [viewType]
  });

  // PORT POLLING //
  // if log publisher enabled, poll port and log publisher state
  const handleFetchPortStatus = async (portId?: number) => {
    const portStatus = await dispatch(fetchPortStatus({ portId }));
    if (portStatus && selectedAutostoreGridId && !!portStatus.selectedBin) {
      dispatch(setPortStatus(portStatus));
      try {
        const binStateResponse = await dispatch(
          fetchBinLogPublisherState(
            selectedAutostoreGridId,
            portStatus.selectedBin
          )
        );
        if (
          binStateResponse?.binState.binMode === "O" &&
          binStateResponse.binState.portId === (portId || selectedPortId)
        ) {
          if (
            isInventoryAdjustDialogOpen &&
            portStatus.selectedBin === firstPort?.getPortResponse.selectedBin
          ) {
            dispatch(setIsCreateInventoryButtonEnabled(true));
            dispatch(setIsFetchingBin(false));
          }
          if (!isAdjustingBins) {
            dispatch(binAtPortEvent());
          }
        }
      } catch {
        resetView(false);
        if (isAutostorePutawayDialogOpen || isInventoryAdjustDialogOpen) {
          dispatch(setIsAutostorePutawayDialogOpen(false));
          dispatch(setIsInventoryAdjustDialogOpen(false));
        }
      }
    }
  };

  const passiveGridPolling =
    !shouldListenToGridEvents && !areAllPortsReady && isAdjustingBins;

  // POLL FOR BIN AT PORT INFORMATION IF EVENT ISN'T RECEIVED LONGER THAN 7 SECONDS
  usePromiseInterval(
    async () => {
      dispatch(incrementBinAtPortSeconds());
      // for grids in passive mode, dont bother waiting for events for 7 seconds
      if (siteWorkstation && (binAtPortSeconds > 7 || passiveGridPolling)) {
        await Promise.all(
          siteWorkstation.ports.map((port) => {
            const portStateResponse =
              portStateByPort[port.portId]?.getPortResponse;
            if (
              !portStateResponse?.isReady &&
              portStateResponse?.selectedBin !== 0
            )
              return handleFetchPortStatus(port.portId);
            return Promise.resolve();
          })
        );
      }
    },
    1000,
    !areAllPortsReady &&
      isAutostoreInventoryView &&
      (isAdjustingBins || !isCreateInventoryButtonEnabled)
  );

  useEffect(() => {
    if (!isAdjustingBins && areAllPortsReady && binAtPortSeconds > 0) {
      dispatch(resetBinAtPortSeconds());
      dispatch(setIsCreateInventoryButtonEnabled(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllPortsReady]);

  const resetPage = useCallback(
    async (clearSelectedSummaries = true) => {
      if (thisWorkstationId) {
        await dispatch(closeWorkstation());
      }
      dispatch(clearSelectedVariant());
      dispatch(clearInventory());
      if (clearSelectedSummaries) {
        dispatch(setSelectedSummaries([]));
      }
      dispatch(setIsAdjustingBins(false));
      dispatch(setSelectedAdjustingSummary(null));
      dispatch(setPortPollingActive(false));
      dispatch(setIsFetchingBin(false));
      dispatch(setAllInventoryPage(1));
      await handleGetInventorySummaries({});
    },
    [dispatch, handleGetInventorySummaries, thisWorkstationId]
  );

  // if warehouse returns a 1015, then no more tasks in task group
  // reset state and refetch inventory summaries
  useEffect(() => {
    if (!userMessages) return;
    const messageExists: boolean | null = userMessages.some(
      (messageObj) =>
        messageObj?.title
          ?.toLowerCase()
          .includes("there is nothing left to pick") || null
    );

    // TO DO: REPLACE LOGIC THAT DEPENDS ON ERROR MESSAGE
    if (!!userMessages.length && messageExists) {
      void (async () => {
        successToast(t("no tasks remaining in current task-group"), {
          duration: 2000
        });

        await dispatch(closeWorkstation());
        await resetPage();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMessages]);

  // Cleanup task group on page render (first hit on page or on page refresh)
  useEffect(() => {
    const closeTaskGroupandWorkstation = async () => {
      if (!thisWorkstationId) return;
      await dispatch(closeWorkstation());
    };
    void closeTaskGroupandWorkstation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClosePort = useCallback(async () => {
    const data = await dispatch(fetchPortStatus());

    if (data && (data.selectedBin > 0 || data.selectedTask > 0)) {
      setIsNavigateAwayModalOpen(true);
    } else {
      await dispatch(closePort());
      dispatch(setIsFetchingBin(false));
      await resetPage();
    }
  }, [dispatch, resetPage]);

  useEffect(() => {
    setMenuItems(
      isAutostoreInventoryView && selectedAutostoreGridId && selectedPortId
        ? [
            {
              textContent: t("close workstation"),
              actionCb: handleClosePort
            }
          ]
        : []
    );
  }, [
    handleClosePort,
    search,
    selectedAutostoreGridId,
    selectedPortId,
    setMenuItems,
    isAutostoreInventoryView,
    t
  ]);

  const warning =
    isAutostoreView(search) && !selectedPortId
      ? t("no workstation selected")
      : null;

  const handleExitGetBins = async () => {
    if (selectedAutostoreGridId && portState) {
      const { selectedTask, isReady } = portState;
      if (isReady) {
        await dispatch(
          closeBin({
            binId: portState.selectedBin,
            taskId: selectedTask
          })
        );
      }
      await dispatch(closePort());
    }
    dispatch(clearInventoryToModify());
    dispatch(setIsAdjustingBins(false));
    dispatch(setPortPollingActive(false));
    dispatch(setIsFetchingBin(false));
  };

  const handleGetBinsMultiPort = async (
    specificBinIds?: number[]
  ): Promise<void> => {
    dispatch(setIsGetBinsLoading(true));
    if (!selectedAutostoreGridId || !thisWorkstationId || !siteWorkstation)
      return;

    await dispatch(
      requestBinsForMultiport(
        specificBinIds || selectedBinNumbers,
        selectedAutostoreGridId,
        thisWorkstationId
      )
    );

    if (!isFetchingBin) {
      dispatch(setIsFetchingBin(true));
    }

    for (const port of siteWorkstation.ports) {
      const portId = port.portId;
      try {
        await dispatch(getNextBinWithConfig({ portId }));
      } catch {
        return;
      }

      await dispatch(fetchPortStatus({ portId })).then(
        (getPortResponse: GetPortResponse | void) => {
          if (!getPortResponse) return;
          const portIsOpen = getPortResponse.mode
            .toLowerCase()
            .includes("open");
          const portIsReady = getPortResponse.isReady;

          if (!portIsReady && shouldListenToGridEvents) {
            dispatch(binNotAtPort());
          }

          if (portIsOpen && portIsReady) {
            try {
              setIsNoInventoryBin(true);
              if (!shouldListenToGridEvents) {
                dispatch(setPortPollingActive(true));
              } else {
                dispatch(binNotAtPort());
              }
              dispatch(setIsFetchingBin(false));
            } catch {
              resetView(false);
              dispatch(resetBinAtPortSeconds());
              if (isInventoryAdjustDialogOpen) {
                dispatch(setIsInventoryAdjustDialogOpen(false));
              }
            }
          }
        }
      );
    }
  };

  const handleCreateBinInventory = async () => {
    if (selectedAutostoreGridId && binNumberParam && thisWorkstationId) {
      dispatch(clearSelectedVariant());
      dispatch(setSelectedSummaries([]));
      dispatch(setIsGetBinsLoading(true));
      dispatch(setIsAutostorePutawayDialogOpen(true));
      // turn on port polling
      dispatch(setIsAdjustingBins(true));
      // fetch current bin
      // how do we fetch the current selected bin when we can search multiples?

      await handleGetBinsMultiPort([Number(binNumberParam)]);
      dispatch(setIsGetBinsLoading(false));
      if (binNumberParam) setSpecificBinAtPort(Number(binNumberParam));
    }
  };

  // listen for portState changes
  useEffect(() => {
    if (!areAllPortsReady) return;
    if (
      (!shouldListenToGridEvents && areAllPortsReady) ||
      (areAllPortsReady && shouldListenToGridEvents)
    ) {
      dispatch(setPortPollingActive(false));
      dispatch(setIsFetchingBin(false));
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    } else if (areAllPortsReady && isNoInventoryBin) {
      dispatch(setPortPollingActive(false));
      dispatch(setIsFetchingBin(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoInventoryBin, areAllPortsReady]);

  const gridSub = (data: AutostoreEvent) => {
    if (
      [
        "LiftCellLocked",
        "LiftCellUnlocked",
        "CellLocked",
        "CellUnlocked"
      ].includes(data.case)
    ) {
      dispatch(warehouseApi.util.invalidateTags(["autostore bin"]));
    }

    if (
      data.case !== "BinModeChange" ||
      data.event.binMode !== "O" ||
      data.event.gridId !== selectedAutostoreGridId ||
      !data.event.portId ||
      !siteAllPortIds.includes(data.event.portId)
    )
      return;

    if (data.event.binId === selectedAutostoreBinId) {
      dispatch(binAtPortEvent());
    }

    // this means we are waiting for empty bins
    // if we receive a bin where all compartments have inventory
    // then we should close the bin and get the next one
    // TODO: make this work when polling
    const emptyBinForPort = nextEmptyBinByPort[data.event.portId];

    const compartmentOccupancy = getBinCompartmentOccupancy(emptyBinForPort);

    if (
      isChangeBinConfigModalOpen &&
      emptyBinForPort &&
      compartmentOccupancy.every((c) => !c.isCompartmentEmpty)
    ) {
      Sentry.captureMessage(
        `[Autostore-inventory] Bin was brought to induction but inventory records were found in bin ${data.event.binId}`,
        "info"
      );

      void (async () => {
        await dispatch(
          closeBin({
            portId: data.event.portId,
            binId: data.event.binId,
            taskId: emptyBinForPort.openBinResponse.taskId
          })
        );
        dispatch(setIsAdjustingBins(true));
        await nextEmptyBin(data.event.portId);
        await handleFetchPortStatus(data.event.portId);
      })();
    }
  };

  useGridV2Subscription(gridSub);

  useEffect(() => {
    if (
      portStateByPortArray.length &&
      portStateByPortArray.every(
        (port) =>
          port.getPortResponse.mode === "OPEN" &&
          !!port.getPortResponse?.selectedBin
      )
    ) {
      dispatch(setIsCreateInventoryButtonEnabled(true));
    }
    // if there's only one putaway default to that task
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySummaries]);

  const handleScanChoiceSelect = (value: string) => {
    const selectedProductResult: BinOrProductResult | undefined =
      scanChoices.find((scanChoice) => scanChoice.variantId === value);

    if (selectedProductResult) {
      handleAutocompleteSelect(selectedProductResult);
    }
  };

  const handleFetchAllInventory = async (clearSelectedSummaries = true) => {
    clearSelectedData();
    navigate({
      pathname: `/autostore-inventory/${isHoldsPage ? "holds" : ""}`,
      search
    });
    await resetPage(clearSelectedSummaries);
  };

  return (
    <ASDSThemeProvider>
      {warning && (
        <ASDSThemeProvider>
          <AlertBanner sx={{ width: "100%", borderRadius: 0 }}>
            {warning}
          </AlertBanner>
        </ASDSThemeProvider>
      )}
      <FullPageLayout
        gridTemplateRows={{
          xs: "auto",
          lg: "auto minmax(450px, 1fr) auto minmax(auto, 0.25fr)",
          xl: "auto minmax(520px, 1fr) minmax(auto, 32px) minmax(auto, 0.3fr)"
        }}
        gridTemplateColumns={{
          default: {
            xs: "1fr",
            // lg: fluid left column (min 280px, target 26%, cap 500px)
            lg: "clamp(280px, 26%, 500px) 1fr",
            // xl: slightly tighter % so it doesn’t grow too wide (min 380px, target 32%, cap 500px)
            xl: "clamp(380px, 32%, 500px) 1fr"
          },
          fallback: {
            xs: "1fr",
            lg: "minmax(280px, 500px) 1fr",
            xl: "minmax(380px, 500px) 1fr"
          }
        }}
      >
        <Stack
          sx={{
            gridRow: "1/-1",
            gridColumn: { xs: "1/-1", md: "1/2" },
            flexDirection: "column",
            gap: 2
          }}
        >
          <InventoryProductCard
            variantId={
              isAnySelect || isProductView
                ? selectedVariant?.variantId
                : undefined
            }
            addClickCb={() => null}
            inventoryReports={inventoryReports}
            initialCycleCountFrequency={initialFrequency}
            hideAddInventory
            sx={{
              overflow: "auto"
            }}
          />
          {selectedVariant?.isSanitizeTargetBin && (
            <Stack direction="row" spacing={1} mt={2}>
              <LeafIcon sx={{ color: palette.infoPurple.dark }} />
              <Typography variant="body1">
                {t("this item requires a sanitized bin")}
              </Typography>
            </Stack>
          )}
          <CycleCountFrequencyButton
            disabled={
              !selectedVariant?.variantId || selectedSummaries.length > 1
            }
            initialFrequency={initialFrequency}
          />
        </Stack>

        {/* Right Side - Buttons + Inventory Table + MultiPort */}
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack>
            <InventorySummaryHeader
              selectedVariant={selectedVariant}
              isAdjustingBins={isAdjustingBins}
              handleCreateBinInventory={handleCreateBinInventory}
              handleFetchAllInventory={handleFetchAllInventory}
              isAutostoreInventoryView={isAutostoreInventoryView}
              binNumberParam={binNumberParam}
              variantIdParam={variantIdParam}
              searchedBinRecord={searchedBinIds}
              binCreateInventoryDisabled={inventorySummaries.some(
                (summary) => summary.isBinFull === true
              )}
              selectedAutostoreGridId={selectedAutostoreGridId}
            />
          </Stack>
          <Stack flexDirection="row" gap={2}>
            <TopActionButtons isAdjustingBins={isAdjustingBins} />
          </Stack>
        </Stack>
        <InventoryTables
          portStateByPortArray={portStateByPortArray}
          summariesToDisplay={summariesToDisplay}
          selectedAutostoreGridId={selectedAutostoreGridId}
          isGetBinsLoading={isGetBinsLoading}
          handleFetchAllInventory={() =>
            handleFetchAllInventory(isAdjustingBins)
          }
          hasInventoryTableV2EnhancedFlag={hasInventoryTableV2EnhancedFlag}
        />
        {siteWorkstation && (
          <BottomActionButtons
            isAdjustingBins={isAdjustingBins}
            isGetBinsDisabled={
              !isAnySelect || !allSelectedBinTypeAreAutostore || isFetchingBin
            }
            selectedSummaries={selectedSummaries}
            inventorySummaryToDisplay={inventorySummaryToDisplay}
            selectedInventoryAtPort={selectedInventoryAtPort}
            handleGetBinsMultiPort={handleGetBinsMultiPort}
            handleGetInventorySummaries={handleGetInventorySummaries}
            searchedBinIds={searchedBinIds}
          />
        )}
        {/* multi ports display as full workstation, span the full page here */}
        <InventoryMultiPort />
      </FullPageLayout>

      {/* Modals */}
      <AutostorePutawayModal
        handleRefetchInventory={handleRefetchInventory}
        resetView={resetView}
      />
      <InventoryAdjustDialog
        handleFetchAllInventory={handleFetchAllInventory}
        handleFetchPortStatus={handleFetchPortStatus}
        onClose={async () => {
          if (!isAdjustingBins) {
            await handleExitGetBins();
          }
        }}
      />
      <BigChoiceModal
        isOpen={showScanChoiceModal}
        choiceTitle="Select Variant"
        choices={scanChoices.map((scanChoice) => ({
          label: scanChoice.displayText,
          value: scanChoice.variantId || ""
        }))}
        selectCb={(selected) => {
          handleScanChoiceSelect(selected);
        }}
        closeCb={() => setShowScanChoiceModal(false)}
      />
      <ConfirmationModal
        isOpen={isNavigateAwayModalOpen}
        labelledById="close-workstation-modal-title"
        confirmCb={async () => {
          await dispatch(closePort());
          if (areAllPortsReady) {
            dispatch(binNotAtPort());
          }
          setIsNavigateAwayModalOpen(false);

          await resetPage();
        }}
        closeCb={() => {
          setIsNavigateAwayModalOpen(false);
        }}
        modalTitle={t("are you sure you want to close the workstation")}
        modalText=""
      />
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={scannedBarcode}
        placeholderText={t("scan product or bin barcode")}
      />
    </ASDSThemeProvider>
  );
}
